import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, KeyRound, Loader2, Plus, RefreshCw, Upload, X } from 'lucide-react';
import { apiService, BoxKeyDTO, BoxKeyResult, PackGroupDTO } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { projectMatches } from '@/config/games';
import { PackThumbnail } from '@/components/config/PackThumbnail';
import { suggestKeyId } from './lootFormat';
import styles from '@/pages/config/LootboxPage.module.scss';

// ============================================================
// Clés d'une box (onglet Box de la page Lootbox / Box)
// ============================================================
// Une clé est un ITEM du behavior pack qui porte premium:lootbox_key { Identifier: <Id de la box> }
// (alias premium:box_key relu). Elle vit dans un groupe de packs, pas dans les containers : la liste
// vient du groupe choisi (par défaut celui marqué « cosmetics » du projet, qui porte le
// PremiumBehaviorPack et le PremiumResourcePack), et la création écrit dans son working tree. Le
// résultat part avec le circuit normal des packs : bouton Envoyer, puis acceptation de la demande.
// ============================================================

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

/** Image PNG choisie : le fichier et son aperçu (data URL). */
interface PngPick { name: string; data: string }

function PngField({ label, hint, value, onChange }: {
  label: string; hint: string; value: PngPick | null; onChange: (v: PngPick | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      <div className={styles.pngRow}>
        {value
          ? <img className={styles.pngPreview} src={value.data} alt={value.name} />
          : <span className={styles.pngEmpty} />}
        <button type="button" className={styles.secondaryBtn} onClick={() => input.current?.click()}>
          <Upload size={13} /> {value ? 'Changer' : 'Choisir un PNG'}
        </button>
        {value && (
          <button type="button" className={styles.iconBtn} title="Retirer" onClick={() => onChange(null)}><X size={13} /></button>
        )}
        <span className={styles.muted}>{value ? value.name : hint}</span>
      </div>
      <input ref={input} type="file" accept="image/png" hidden
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onChange({ name: f.name, data: await readAsDataUrl(f) });
        }} />
    </div>
  );
}

export function BoxKeysPanel({ boxId }: { boxId: string | undefined }) {
  const project = useGameStore((s) => s.activeGameId);
  const [groups, setGroups] = useState<PackGroupDTO[]>([]);
  const [groupId, setGroupId] = useState('');
  const [keys, setKeys] = useState<BoxKeyDTO[]>([]);
  const [readonly, setReadonly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<BoxKeyResult | null>(null);
  // Formulaire de création
  const [identifier, setIdentifier] = useState('');
  const [nameFr, setNameFr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [icon, setIcon] = useState<PngPick | null>(null);
  const [withAttachable, setWithAttachable] = useState(true);
  const [template, setTemplate] = useState('');
  const [modelTexture, setModelTexture] = useState<PngPick | null>(null);
  const seq = useRef(0);

  // Groupes du projet dont le resource pack est modifiable ; par défaut celui des cosmetics
  // (le groupe premium sur Paladium), jamais un nom codé en dur.
  useEffect(() => {
    let active = true;
    apiService.getPacksGroups().then((r) => {
      if (!active) return;
      const list = (r.data?.groups ?? []).filter((g) => projectMatches(g.projects, project) && !g.resourceReadonly);
      setGroups(list);
      setGroupId((cur) => (list.some((g) => g.id === cur) ? cur : (list.find((g) => g.cosmeticsSource) ?? list[0])?.id ?? ''));
    });
    return () => { active = false; };
  }, [project]);

  const load = useCallback(async () => {
    if (!groupId) { setKeys([]); return; }
    const s = ++seq.current;
    setLoading(true);
    setError(null);
    const r = await apiService.getBoxKeys(groupId);
    if (s !== seq.current) return;
    setLoading(false);
    if (!r.data) { setError(r.error ?? 'Lecture des clés impossible.'); setKeys([]); return; }
    setKeys(r.data.keys);
    setReadonly(r.data.resourceReadonly);
    if (!r.data.exists) setError('Groupe non synchronisé : synchronise-le dans Paramètres → Pack.');
  }, [groupId]);

  useEffect(() => { void load(); }, [load]);

  const mine = useMemo(() => keys.filter((k) => boxId && k.box === boxId), [keys, boxId]);
  const others = keys.length - mine.length;
  const templates = useMemo(() => keys.filter((k) => k.attachable), [keys]);
  const group = groups.find((g) => g.id === groupId);

  // Nouvelle box ouverte : formulaire repris de zéro, identifiant proposé d'après l'Id de la box.
  useEffect(() => {
    setResult(null);
    setFormOpen(false);
    setIdentifier(boxId ? suggestKeyId(boxId) : '');
    setNameFr('');
    setNameEn('');
    setIcon(null);
    setModelTexture(null);
  }, [boxId]);

  useEffect(() => {
    setTemplate((cur) => (templates.some((t) => t.identifier === cur) ? cur : templates[0]?.identifier ?? ''));
  }, [templates]);

  const create = async () => {
    if (!boxId || !groupId) return;
    if (!icon) { setResult({ success: false, message: 'Icône PNG obligatoire.', identifier: null, files: [] }); return; }
    setCreating(true);
    setResult(null);
    const names: Record<string, string> = {};
    if (nameFr.trim()) names.fr_FR = nameFr.trim();
    if (nameEn.trim()) names.en_US = nameEn.trim();
    const r = await apiService.createBoxKey(groupId, {
      identifier: identifier.trim(),
      box: boxId,
      names,
      iconData: icon.data,
      attachable: withAttachable && templates.length > 0,
      templateKey: withAttachable ? template || undefined : undefined,
      modelTextureData: withAttachable ? modelTexture?.data : undefined,
    });
    setCreating(false);
    const res = r.data ?? { success: false, message: r.error ?? 'Création impossible.', identifier: null, files: [] };
    setResult(res);
    if (res.success) {
      setFormOpen(false);
      setIcon(null);
      setModelTexture(null);
      void load();
    }
  };

  return (
    <div className={styles.keysPanel}>
      <div className={styles.keysHead}>
        <span className={styles.keysTitle}><KeyRound size={15} /> Clés de cette box</span>
        <label className={styles.keysGroup} title="Groupe de packs où vivent les clés (behavior + resource)">
          <span className={styles.label}>Groupe de packs</span>
          <select className={styles.input} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            {groups.length === 0 && <option value="">aucun groupe modifiable</option>}
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name || g.id}{g.cosmeticsSource ? ' (cosmetics)' : ''}</option>
            ))}
          </select>
        </label>
        <button className={styles.iconBtn} title="Relire les clés du groupe" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 size={13} className={styles.spin} /> : <RefreshCw size={13} />}
        </button>
        <button className={styles.secondaryBtn} disabled={!boxId || !groupId || readonly}
          title={!boxId ? "Enregistre d'abord un Id pour cette box" : readonly ? 'Resource pack en lecture seule' : undefined}
          onClick={() => { setFormOpen((o) => !o); setResult(null); }}>
          <Plus size={13} /> Créer une clé
        </button>
      </div>

      {error && <div className={styles.error}><AlertTriangle size={14} /> {error}</div>}
      {!boxId && <p className={styles.muted}>Cette box n'a pas d'Id enregistré : aucune clé ne peut la désigner.</p>}

      {boxId && !error && (
        mine.length === 0 ? (
          <p className={styles.muted}>
            Aucune clé n'ouvre {boxId} dans le groupe {group?.name ?? groupId}
            {others > 0 ? ` (${others} clé(s) pour d'autres box).` : '.'}
          </p>
        ) : (
          <div className={styles.keysList}>
            {mine.map((k) => (
              <div key={k.identifier} className={styles.keyRow} title={k.source}>
                <PackThumbnail groupId={groupId} path={k.iconPath} size={32} />
                <span className={styles.keyMain}>
                  <strong>{k.identifier}</strong>
                  <span className={styles.muted}>
                    {Object.entries(k.names).map(([l, n]) => `${l} : ${n}`).join(' · ') || 'sans nom affiché'}
                  </span>
                </span>
                <span className={styles.rarityTotal}>{k.attachable ? 'attachable 3D' : 'sans attachable'}</span>
                {k.component !== 'premium:lootbox_key' && <span className={styles.rarityTotal}>{k.component}</span>}
              </div>
            ))}
            {others > 0 && <span className={styles.muted}>{others} autre(s) clé(s) dans ce groupe pour d'autres box.</span>}
          </div>
        )
      )}

      {result && (
        <div className={result.success ? styles.ok : styles.error}>
          {result.success ? <Check size={14} /> : <AlertTriangle size={14} />}
          <span>
            {result.message}
            {result.files.length > 0 && <><br /><span className={styles.muted}>{result.files.join(', ')}</span></>}
          </span>
        </div>
      )}

      {formOpen && boxId && (
        <div className={styles.keyForm}>
          <div className={styles.detailsGrid}>
            <label className={styles.field}>
              <span className={styles.label}>Identifiant de la clé</span>
              <input className={styles.input} value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                placeholder="paladium:amethyst_key_lootbox" />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Nom affiché (fr_FR)</span>
              <input className={styles.input} value={nameFr} onChange={(e) => setNameFr(e.target.value)} placeholder="§5Clé en Améthyste" />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Nom affiché (en_US)</span>
              <input className={styles.input} value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder="§5Amethyst Key" />
            </label>
          </div>
          <PngField label="Icône (inventaire)" hint="16x16 comme les clés existantes" value={icon} onChange={setIcon} />
          <label className={styles.checkLine}>
            <input type="checkbox" checked={withAttachable && templates.length > 0} disabled={templates.length === 0}
              onChange={(e) => setWithAttachable(e.target.checked)} />
            Attachable 3D (clé tenue en main)
            {templates.length === 0 && <span className={styles.muted}> : aucune clé du groupe n'en a un à copier</span>}
          </label>
          {withAttachable && templates.length > 0 && (
            <div className={styles.detailsGrid}>
              <label className={styles.field}>
                <span className={styles.label}>Modèle (géométrie et animations reprises)</span>
                <select className={styles.input} value={template} onChange={(e) => setTemplate(e.target.value)}>
                  {templates.map((t) => <option key={t.identifier} value={t.identifier}>{t.identifier}</option>)}
                </select>
              </label>
              <PngField label="Texture du modèle 3D" hint="32x32 ; sans fichier, celle du modèle est reprise"
                value={modelTexture} onChange={setModelTexture} />
            </div>
          )}
          <p className={styles.muted}>
            Écrit dans le groupe {group?.name ?? groupId} : items/{identifier.split(':').pop() || '…'}.json (composant
            premium:lootbox_key vers {boxId}, pile de 64), l'icône dans item_texture.json, les noms dans texts/*.lang
            {withAttachable && templates.length > 0 ? ' et l\'attachable' : ''}. Rien n'est publié : envoie ensuite le
            groupe avec le bouton Envoyer.
          </p>
          <div className={styles.headerActions}>
            <button className={styles.primaryBtn} onClick={() => void create()}
              disabled={creating || !identifier.trim() || !icon || (!nameFr.trim() && !nameEn.trim())}>
              {creating ? <Loader2 size={14} className={styles.spin} /> : <KeyRound size={14} />} Créer la clé
            </button>
            <button className={styles.secondaryBtn} onClick={() => setFormOpen(false)}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}
